import { useEffect } from 'react';
import type { StorageKeyBuilder } from '../../../context/use-storage-key';
import { useStepStatus } from '../engine/use-step-status';
import type { EffectiveChecklistItemStatus, UseChecklistItemStatus } from '../types';

export type StatusProbeProps = {
  id: string;
  useStatus: UseChecklistItemStatus;
  /** Builds the skip key, so the probe folds skip in exactly as the rendered item does. */
  storageKeyBuilder?: StorageKeyBuilder;
  /** Report this probe's latest (effective) status into a registry. */
  onStatus: (id: string, status: EffectiveChecklistItemStatus | undefined) => void;
  /** Drop this probe from the registry on unmount. */
  onUnmount: (id: string) => void;
};

/**
 * Renders nothing — it exists only to resolve a single step's effective status and report it. One
 * probe per step is the Rules-of-Hooks-safe way to evaluate a variable-length list of hook-backed
 * statuses: each probe is its own component, so it makes exactly one set of hook calls.
 */
export function StatusProbe({
  id,
  useStatus,
  storageKeyBuilder,
  onStatus,
  onUnmount,
}: StatusProbeProps) {
  const { status } = useStepStatus(id, useStatus, storageKeyBuilder);

  useEffect(() => {
    onStatus(id, status);
  }, [id, status, onStatus]);

  // Unmount-only (keyed on `id`) so a status change doesn't drop the registry slot.
  useEffect(() => {
    return () => onUnmount(id);
  }, [id, onUnmount]);

  return null;
}
